import {stringifyId} from '../../../../types'
import type {ProblemsTreeNodeId, BuildId} from '../../../../types'
import type {KeyValue, WritableKeyValue} from '../../../../utils/object'
import type {Node, NodeValue} from '../../../common/Tree/Tree.types'
import {ErrorTypes} from '../BuildProblems.constants'
import type {ProblemOccurrencesTreeLeaf, ProblemOccurrencesTreeNode} from '../BuildProblems.types'
import {TreeEntityType} from '../BuildProblems.types'

import type {AdvancedNode} from './BuildProblemsTree.types'
import ProblemsTreeLeaf from './BuildProblemsTreeLeaf/BuildProblemsTreeLeaf'
import ProblemsTreeLeafDetails from './BuildProblemsTreeLeafDetails/BuildProblemsTreeLeafDetails'
import ProblemsTreeNode from './BuildProblemsTreeNode/BuildProblemsTreeNode'
import BuildProblemsTreeNodeFooter from './BuildProblemsTreeNodeFooter/BuildProblemsTreeNodeFooter'

const config: Record<TreeEntityType, TreeEntityType[]> = {
  [TreeEntityType.PROJECT]: [
    TreeEntityType.PROJECT,
    TreeEntityType.BUILD_TYPE,
    TreeEntityType.BUILD,
  ],
  [TreeEntityType.BUILD_TYPE]: [TreeEntityType.BUILD_TYPE, TreeEntityType.BUILD],
  [TreeEntityType.BUILD]: [],
  [TreeEntityType.PROBLEM_TYPE]: [],
}

export const generateProblemsTree = (
  dataNodes: ReadonlyArray<ProblemOccurrencesTreeNode>,
  dataLeavesHash: KeyValue<ProblemsTreeNodeId, ProblemOccurrencesTreeLeaf>,
  treeLocator: string,
  buildId: BuildId | undefined,
): Node | null => {
  let root = null
  let buildsCount = 0
  let hasCurrentBuild = false
  const map: WritableKeyValue<string, number> = {}
  const advancedDataNodeHash: WritableKeyValue<NodeValue, AdvancedNode> = {}

  if (dataNodes.length === 0) {
    return null
  }

  const nodes: ReadonlyArray<Node> = dataNodes.map((dataNode, index) => {
    const {id, name, type} = dataNode
    map[id] = index
    advancedDataNodeHash[id] = {
      ...dataNode,
      abstractParents: [],
      allParents: [],
    }
    if (type === TreeEntityType.BUILD) {
      hasCurrentBuild = name === stringifyId(buildId)
      buildsCount += 1
    }
    return {
      value: id,
      label: name,
      children: [], // Default value
    }
  })

  const simplifyTree = buildsCount === 1 && hasCurrentBuild

  for (let index = 0; index < dataNodes.length; index++) {
    const {parentId, type} = dataNodes[index]
    if (
      !simplifyTree ||
      (simplifyTree && type !== TreeEntityType.PROJECT && type !== TreeEntityType.BUILD_TYPE)
    ) {
      const node = nodes[index]
      const packageIndex = map[stringifyId(parentId)]

      if (parentId == null || (simplifyTree && type === TreeEntityType.BUILD)) {
        root = node
      } else {
        const children = packageIndex != null ? nodes[packageIndex].children : null
        if (Array.isArray(children)) {
          children.push(node)
        }
      }
    }
  }

  const findAllParents = (node: Node): void => {
    if (node.children != null) {
      const advancedDataNode = advancedDataNodeHash[node.value]
      if (advancedDataNode == null) {
        return
      }

      const {allParents = [], ...abstractNode} = advancedDataNode

      node.children.forEach(child => {
        const childNode = advancedDataNodeHash[child.value]
        if (childNode != null) {
          childNode.allParents = [...allParents, abstractNode]
          findAllParents(child)
        }
      })
    }
  }

  if (root != null) {
    findAllParents(root)
  }

  const findNonAbstractChild = (node: Node): Node => {
    if (node.children != null) {
      const child = node.children[0]
      const advancedDataNode = advancedDataNodeHash[node.value]

      if (advancedDataNode == null) {
        return node
      }

      const {abstractParents = [], ...abstractNode} = advancedDataNode
      const childNode = advancedDataNodeHash[child.value]

      if (childNode != null && config[abstractNode.type].includes(childNode.type)) {
        childNode.abstractParents = [...abstractParents, abstractNode]
        return child.children?.length === 1 ? findNonAbstractChild(child) : child
      }
    }

    return node
  }

  const removeAbstractParents = (children: Node[] = []) => {
    for (let i = 0; i < children.length; i++) {
      const node = children[i]

      if (node.children != null && node.children.length === 1) {
        children.splice(i, 1, findNonAbstractChild(node))
      }

      removeAbstractParents(children[i].children)
    }
  }

  const addComponents = (parent: Node) => {
    const {children} = parent

    if (children == null) {
      return
    }

    for (let i = 0; i < children.length; i++) {
      const advancedDataNode = advancedDataNodeHash[children[i].value]

      if (advancedDataNode != null) {
        const {type, id, count, childrenCount, allParents = []} = advancedDataNode

        if (type === TreeEntityType.PROBLEM_TYPE) {
          const problemOccurrences = dataLeavesHash[id]?.problemOccurrences?.problemOccurrence ?? []
          children[i].children = problemOccurrences.map(problemOccurrence => {
            const {id: problemOccurrenceId, type: problemOccurrenceType} = problemOccurrence
            const expandable =
              problemOccurrenceType !== ErrorTypes.SNAPSHOT_DEPENDENCY_ERROR &&
              problemOccurrenceType !== ErrorTypes.SNAPSHOT_DEPENDENCY_ERROR_BUILD_PROCEEDS_TYPE
            return {
              value: stringifyId(problemOccurrenceId),
              label: problemOccurrence?.problem?.description,
              expandable,
              component: <ProblemsTreeLeaf problemOccurrenceId={problemOccurrenceId!} />,
              detailsComponent: (
                <ProblemsTreeLeafDetails problemOccurrenceId={problemOccurrenceId!} />
              ),
            }
          })
        }

        children[i] = {
          ...children[i],
          component: <ProblemsTreeNode dataNode={advancedDataNode} />,
          footerComponent: (
            <BuildProblemsTreeNodeFooter
              node={children[i]}
              depth={allParents.length}
              type={type}
              childrenCount={childrenCount}
              count={count}
              treeLocator={treeLocator}
            />
          ),
        }

        if (advancedDataNode.type !== TreeEntityType.PROBLEM_TYPE) {
          addComponents(children[i])
        }
      }
    }
  }

  removeAbstractParents(root?.children)
  if (root) {
    const advancedDataNode = advancedDataNodeHash[root.value]

    if (advancedDataNode != null) {
      const {type, childrenCount, count, allParents = []} = advancedDataNode
      root.footerComponent = (
        <BuildProblemsTreeNodeFooter
          node={root}
          depth={allParents.length}
          type={type}
          childrenCount={childrenCount}
          count={count}
          treeLocator={treeLocator}
        />
      )
    }

    addComponents(root)
  }

  return root
}
